import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { ChangeDetectionStrategy, Component, HostBinding, Input, OnDestroy } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NonNullableFormBuilder,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { extractErrors } from '@vsolv/dev-kit/ngx';
import { Payment } from '@vsolv/packages/payments/domain';
import { ReplaySubject, Subject, takeUntil } from 'rxjs';

export type BankAccountInputValue = null | {
  nameOnAccount: string;
  bankName: string;
  accountType: Payment.BankAccountType;
  routingNumber: string;
  accountNumber: string;
};

@Component({
  selector: 'vs-bank-account-input',
  templateUrl: './bank-account-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: BankAccountInputComponent, multi: true },
    { provide: NG_VALIDATORS, useExisting: BankAccountInputComponent, multi: true },
  ],
})
export class BankAccountInputComponent implements OnDestroy, ControlValueAccessor, Validator {
  @HostBinding('class') protected _classes = 'block [&_vs-form-field[appearance=vertical]>div>div]:max-w-none';

  @Input() set value(value: BankAccountInputValue) {
    if (!value) {
      this.form.patchValue({
        nameOnAccount: '',
        bankName: '',
        accountType: Payment.BankAccountType.checking,
        routingNumber: '',
        accountNumber: '',
        confirmAccountNumber: '',
      });
      return;
    }

    this.form.patchValue({
      ...value,
      // Keep programmatic writes valid for restore/back-navigation flows.
      confirmAccountNumber: value.accountNumber,
    });
  }
  get value(): BankAccountInputValue {
    if (!this.form.valid) return null;

    const value = this.form.getRawValue();
    return {
      nameOnAccount: value.nameOnAccount,
      bankName: value.bankName,
      accountType: value.accountType,
      routingNumber: value.routingNumber,
      accountNumber: value.accountNumber,
    };
  }

  @Input() set disabled(disabled: BooleanInput) {
    if (coerceBooleanProperty(disabled)) this.form.disable();
    else this.form.enable();
  }
  get disabled() {
    return this.form.disabled;
  }

  protected accountTypes = [
    { value: Payment.BankAccountType.checking, label: 'Checking' },
    { value: Payment.BankAccountType.savings, label: 'Savings' },
    { value: Payment.BankAccountType.businessChecking, label: 'Business Checking' },
  ];

  protected form = this.formBuilder.group({
    nameOnAccount: ['', Validators.required],
    bankName: ['', Validators.required],
    accountType: [Payment.BankAccountType.checking, Validators.required],
    routingNumber: ['', [Validators.required, Validators.pattern(/^\d{9}$/)]],
    accountNumber: ['', [Validators.required, Validators.pattern(/^\d{5,17}$/)]],
    confirmAccountNumber: [
      '',
      [
        Validators.required,
        (control: AbstractControl<string>): ValidationErrors | null => {
          if (!control.parent) return null;
          const accountNumber = control.parent.get('accountNumber')?.value;
          if (accountNumber && control.value && accountNumber !== control.value) {
            return { mismatch: true };
          }
          return null;
        },
      ],
    ],
  });

  protected touched$ = new ReplaySubject<void>(1);
  private onDestroy$ = new Subject<void>();

  constructor(private formBuilder: NonNullableFormBuilder) {
    this.form.controls.accountNumber.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.form.controls.confirmAccountNumber.updateValueAndValidity({ onlySelf: true });
    });
  }

  writeValue(value: BankAccountInputValue): void {
    this.value = value;
  }

  registerOnChange(fn: (value: BankAccountInputValue) => void): void {
    this.form.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe(() => fn(this.value));
  }

  registerOnTouched(fn: () => void): void {
    this.touched$.pipe(takeUntil(this.onDestroy$)).subscribe(() => fn());
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  validate(): ValidationErrors | null {
    return extractErrors(this.form);
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();

    this.touched$.complete();
  }
}
